import { FC, useEffect, useState } from "react";
import { MainSidebar } from "../sidebar/SideBar";
import { Navbar } from "../navbar/Navbar";
import { matchPath, useLocation } from "react-router-dom";
import { NavbarContext } from "../../hooks/UseNavbarContext";
import { HIDE_ROUTES, ROUTE_URLS } from "../../constants/consts";
import { getSession, getSessionUserRole } from "../../api/sessionManagement";
import useMessage, { MessageContext } from "../../hooks/UseMessage";
import Message from "../message/Message";
import { LanguageInitializer } from "../LanguageInitializer";

interface ContainerProps {
  children: JSX.Element;
}

export const Container: FC<ContainerProps> = ({ children }) => {
  const { pathname } = useLocation();
  const [navbarBadgeTrigger, setNavbarBadgeTrigger] = useState(false);
  const [shouldRenderMainComponents, setShouldRenderMainComponents] =
    useState<any>(false);
  const { message, showMessage, hideMessage } = useMessage();

  const shouldShowGeneralComponents = () => {
    return !HIDE_ROUTES.includes(pathname as ROUTE_URLS) && getSession();
  };

  const isHomePage = () => {
    return ["/"].includes(pathname);
  };
  const isCatalogRoute = [ROUTE_URLS.CATALOG, ROUTE_URLS.CATALOG_CATEGORY].some(
    (route) => matchPath(route, pathname)
  );
  const isAuthPublicRoute = [
    ROUTE_URLS.SIGN_IN,
    ROUTE_URLS.SIGN_UP,
    ROUTE_URLS.RECOVER_PASSWORD,
    ROUTE_URLS.RECOVER_PASSWORD_FORM,
  ].some((route) => matchPath(route, pathname));
  const isPublicFullWidthRoute = !shouldShowGeneralComponents();

  useEffect(() => {
    const checkHideRoutes = HIDE_ROUTES.some(route => matchPath(route, pathname));
    if (getSession()) {
      if (checkHideRoutes) {
        setShouldRenderMainComponents(!checkHideRoutes);
      } else {
        let conditionForQuestionary = ["/questionary"].includes(pathname);
        if (conditionForQuestionary) {
          setShouldRenderMainComponents(getSessionUserRole() === "active");
          return;
        }
        let conditionForTermServices = ["/terms-service", "/portal-rules", "/warranty-terms", "/warranty-periods", "/warranty-case"].includes(pathname);
        if (conditionForTermServices) {
          setShouldRenderMainComponents(getSessionUserRole() === "active");
          return;
        }
        setShouldRenderMainComponents(true);
      }
    } else {
      setShouldRenderMainComponents(false);
    }
  }, [pathname]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, [pathname]);

  return (
    <LanguageInitializer>
      <div className={`_container ${!shouldShowGeneralComponents() ? "_container--fullbleed" : ""}`.trim()}>
        <div
          className={`${
            !shouldShowGeneralComponents()
              ? ""
              : isHomePage()
              ? "home_page_container trade_container"
              : "trade_container"
          }`}
        >
          {shouldRenderMainComponents && <MainSidebar />}
          <section
            className={`${
              isPublicFullWidthRoute && isAuthPublicRoute
                ? "public-route-shell"
              : isPublicFullWidthRoute
                ? "public-fullwidth-shell"
              : isHomePage()
                ? "home_page trade_section"
                : "trade_section"
            } ${isCatalogRoute ? "trade_section--catalog" : ""}`.trim()}
          >
            <MessageContext.Provider
              value={{ message, showMessage, hideMessage }}
            >
              <NavbarContext.Provider
                value={{ navbarBadgeTrigger, setNavbarBadgeTrigger }}
              >
                <></>
                {shouldRenderMainComponents && (
                  <div
                    className={`navbarTopLayer ${isCatalogRoute ? "navbarTopLayer--catalog" : ""}`.trim()}
                  >
                    <Navbar />
                  </div>
                )}
                {children}
                {message && (
                  <Message
                    children={message.text}
                    shouldRedirectByClick={message.shouldRedirectByClick}
                    redirectPath={message.redirectPath}
                    hideMessage={hideMessage}
                    variant={message.variant}
                    timeout={message.timeout}
                  />
                )}
              </NavbarContext.Provider>
            </MessageContext.Provider>
          </section>
        </div>
      </div>
    </LanguageInitializer>
  );
};
